/**
 * L2-R5: moved verbatim from @shared/utils/tree-node/tree-node.helpers.
 * That host path is now a re-export shim of this file.
 */
import { EMPTY_STRING } from '../misc/strings.constants';
import { RawTreeNode, TreeNode } from './tree-node';

const isNotNull = item => item != null;
/**
 * Creates a leaf or a node of TreeNode if pivot is the last index from the array set
 * @param target {T}
 * @param elements {T} data source
 * @param pivot {number}
 * @param fields {string[]} fields used to build the key needed to group
 */
function buildTreeNodeItem<T = { [key: string]: any }>(target: T, elements: T[], pivot: number, fields: string[]) {
  const field = fields[pivot];

  if (pivot + 1 === fields.length) {
    return new TreeNode(target, field);
  }

  // find children based on next subset of fields
  const fieldSubset = fields.filter((_, i) => i <= pivot);

  const children = elements.filter(item =>
    fieldSubset.every(subsetField => item[subsetField] === target[subsetField] && !!item[fields[pivot + 1]])
  );
  // don't create node if does not have value
  if (target[field] == null) {
    return null;
  }

  // build node
  const node = new TreeNode(target, field);
  if (children && children.length) {
    node.setChildren([buildTreeNodeItem(target, children, pivot + 1, fields)]);
  }
  return node;
}

/**
 * Builds a tree map of TreeNode objects based passed fields array (as levels) and converting each item
 * in elements (an array of {k:v} objects) into TreeNode
 * @param elements
 * @param fields
 */
export function makeTreeNodeObject<T>(elements: T[], fields: string[]): { [key: string]: TreeNode<T> } {
  return (
    elements
      // make the nodes
      .map(item => buildTreeNodeItem(item, elements, 0, fields))
      .filter(isNotNull)
      // start grouping them
      .reduce((acc, nextNode) => {
        const value = nextNode.value;
        const existingNode = acc[value];
        if (!existingNode) {
          // add it as new if not already in acc
          return { ...acc, [value]: nextNode };
        }
        // append each child to existing node
        (nextNode.children || []).filter(isNotNull).forEach(child => existingNode.addChild(child));
        return acc;
      }, {})
  );
}

/**
 * Returns Object.values of a TreeNode map (resultant of calling buildTreeNodeItem)
 * @param elements
 * @param fields
 * @returns TreeNode[]
 */
export function groupTreeNodesByFields<T>(elements: T[], fields: string[]): Array<TreeNode<T>> {
  return Object.values(makeTreeNodeObject(elements, fields));
}

/**
 * Maps the result of calling groupTreeNodesByFields to RawTreeNode[]
 * @param elements
 * @param fields
 * @returns RawTreeNode[]
 */
export function groupDataByFields<T>(elements: T[], fields: string[]): RawTreeNode[] {
  return groupTreeNodesByFields(elements, fields).map(node => node.raw());
}

/************************************************************/
/******************* DISPLAY HELPER *************************/
/************************************************************/

/**
 * Return TreeNode.children if children has items and a mapper function is passed, else return empty string.
 * @param children
 * @param options
 */
const concatenateTreeNodeChildren = (children, options: { separator: string; childMapper?: (x) => string }) =>
  children && children.length && options.childMapper ? ` - ${options.childMapper(children)}` : EMPTY_STRING;

/**
 * Concatenate node.value - concatenateTreeNodeChildren(node.children)
 * @param separator {string}
 * @param childMapper {(x) => string}
 * @param treeNodes {RawTreeNode[]}
 */
const concatenateTreeNodes = (options: { separator: string; childMapper?: (x) => string }, treeNodes: RawTreeNode[]) =>
  treeNodes
    .filter(node => node.value != null)
    .map(node => `${node.value}${concatenateTreeNodeChildren(node.children, options)}`)
    .sort((a, b) => a.localeCompare(b))
    .join(options.separator);

/**
 * Builds a string based resultant treeNode array of grouping by fields provided in fieldSet array
 * @param dataSource
 * @param fieldSet
 *
 * @example
 * const ds = [ {a: 'a1', b: 'b1' }, {a: 'a1', b: 'b2' }, {a: 'a2', b: 'b1' } ]
 * const fieldSet = [{field: 'a', separator: ';; '}, {field: b, separator: ','}]
 * makeGroupedText(ds, fieldSet);
 * //> a1 - b1,b2;; a2 - b1
 */
export function makeTreeNodesAndReturnAsGroupedText<T>(
  dataSource: T[],
  fieldSet: Array<{ field: string; separator: string }>
) {
  const fields = [];
  const rootLevelTextGrouper = fieldSet.reduce((nextTextGrouperFn, { field, separator }) => {
    fields.push(field);
    return concatenateTreeNodes.bind(null, { separator, childMapper: nextTextGrouperFn });
  }, null);
  const groupedItems = groupDataByFields(dataSource, fields);
  return rootLevelTextGrouper(groupedItems);
}
